import React, { useMemo, useState } from "react";

import { Box, Chip, Pagination, Paper, Stack, Typography } from "@mui/material";

import CandidateCard from "./CandidateCard";

import TeamLeadershipPanel from "./TeamLeadershipPanel";

import ProspectiveTeamReview from "./ProspectiveTeamReview";

import {

  buildMockWorkspace,

  computeSummaryFromCandidates,

  DEANERY_UNAVAILABLE_TEXT,

  TEAM_SIZE_GUIDANCE_TEXT,

} from "./teamFormationMockData";

import {

  TeamFormationShell,

  TeamInfoAlert,

  TeamSectionPaper,

  teamApprovedChipSx,

  teamBodySx,

  teamConsiderationChipSx,

  teamOverlineSx,

  teamPanelHeadingSx,

  teamPanelSubtextSx,

  teamRecipientChipSx,

  teamSectionTitleSx,

  teamStatBoxSx,

  TEAM_MUTED,

  TEAM_TEXT,

} from "./teamFormationUi";

import type {

  TeamCandidate,

  TeamFormationSummary,

  TeamLeadershipAssignment,

  TeamReview,

} from "./teamFormationTypes";



type Props = {

  parishName: string;

  seasonLabel: string;

  leadName?: string;

  coLeadName?: string;

  mentorName?: string;

  onSummaryChange?: (summary: TeamFormationSummary) => void;

  onReviewStatusChange?: (status: string | null) => void;

};



function reviewStatusLabel(status: TeamReview["status"] | undefined): string | null {

  if (!status) return null;

  if (status === "PENDING_REVIEW") return "Review pending";

  if (status === "REVIEW_OVERDUE_ESCALATED") return "Review overdue";

  if (status === "CHANGES_REQUESTED") return "Changes requested";

  if (status === "APPROVED" || status === "APPROVED_WITH_CONCERNS") {

    return "Review decided";

  }

  return null;

}



export default function TeamFormationTab({

  parishName,

  seasonLabel,

  leadName,

  coLeadName,

  mentorName,

  onSummaryChange,

  onReviewStatusChange,

}: Props) {

  const initial = useMemo(

    () =>

      buildMockWorkspace({

        leadName,

        coLeadName,

        mentorName,

        retreatLiaisonName: coLeadName || "Miguel & Rosa Santos",

      }),

    [leadName, coLeadName, mentorName],

  );



  const [candidates, setCandidates] = useState<TeamCandidate[]>(

    initial.candidates,

  );

  const [assignments, setAssignments] = useState<TeamLeadershipAssignment[]>(

    initial.leadership.assignments,

  );

  const [reviews, setReviews] = useState<TeamReview[]>(initial.reviews);

  const [page, setPage] = useState(1);



  const summary = useMemo(

    () => computeSummaryFromCandidates(candidates),

    [candidates],

  );



  React.useEffect(() => {

    onSummaryChange?.(summary);

  }, [summary, onSummaryChange]);



  React.useEffect(() => {

    const latest = reviews.at(-1);

    onReviewStatusChange?.(reviewStatusLabel(latest?.status));

  }, [reviews, onReviewStatusChange]);



  const groupedCandidates = useMemo(() => {

    const map = new Map<string, TeamCandidate[]>();

    for (const candidate of candidates) {

      const list = map.get(candidate.priorityLabel) ?? [];

      list.push(candidate);

      map.set(candidate.priorityLabel, list);

    }

    return Array.from(map.entries());

  }, [candidates]);



  const pageSize = initial.pageSize;

  const pageCount = Math.max(1, Math.ceil(candidates.length / pageSize));



  const toggleConsideration = (coupleId: string, inConsideration: boolean) => {

    setCandidates((prev) =>

      prev.map((c) => {

        if (c.coupleId !== coupleId) return c;

        if (c.status === "APPROVED_PROSPECT" || c.status === "SUBMITTED_FOR_REVIEW") {

          return c;

        }

        return {

          ...c,

          status: inConsideration ? "IN_CONSIDERATION" : "AVAILABLE",

        };

      }),

    );

  };



  const assignLeadership = (role: string, principalId: string | number | null) => {

    setAssignments((prev) =>

      prev.map((a) => {

        if (a.role !== role) return a;

        const options =

          role === "SPIRITUAL_MENTOR_COUPLE"

            ? initial.leadership.spiritualMentorOptions

            : initial.leadership.spiritualDirectorOptions;

        const match = options.find((o) => String(o.principalId) === String(principalId));

        return {

          ...a,

          principalId,

          displayName: match?.displayName ?? null,

        };

      }),

    );

  };



  const handlePrepareReview = (payload: {

    reviewBy: string;

    subject: string;

    message: string;

  }) => {

    const inConsiderationIds = candidates

      .filter((c) => c.status === "IN_CONSIDERATION")

      .map((c) => c.coupleId);

    if (!inConsiderationIds.length) return;



    const version = reviews.length + 1;

    setCandidates((prev) =>

      prev.map((c) =>

        c.status === "IN_CONSIDERATION"

          ? { ...c, status: "SUBMITTED_FOR_REVIEW" as const }

          : c,

      ),

    );

    setReviews((prev) => [

      ...prev,

      {

        id: `team-review-${Date.now()}`,

        label:

          version === 1

            ? "Team Prospect Review v1"

            : `Team Prospect Review v${version} - Additional Prospects`,

        status: "PENDING_REVIEW",

        reviewBy: payload.reviewBy,

        communicationStatus: "PREPARED_NOT_DELIVERED",

        candidateIds: inConsiderationIds,

        escalationRecipients: [],

      },

    ]);

  };



  const authorityLabel =

    initial.authority.type === "PARISH_STEERING_COMMITTEE"

      ? "Parish Steering Committee"

      : "Executive Committee";



  return (

    <TeamFormationShell>

      <Stack spacing={2} sx={{ py: 2 }}>

        <TeamSectionPaper>

          <Stack

            direction={{ xs: "column", md: "row" }}

            spacing={1.5}

            sx={{ justifyContent: "space-between", alignItems: { md: "flex-start" } }}

          >

            <Box sx={{ maxWidth: 760 }}>

              <Typography sx={teamSectionTitleSx}>Team Formation</Typography>

              <Typography sx={{ ...teamBodySx, mt: 0.5 }}>

                Build a balanced prospective Team from couples with prior

                LOVESTRONG Retreat attendance. Selection means consideration only.

              </Typography>

            </Box>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>

              <Chip

                label={`${summary.inConsideration} in consideration`}

                sx={teamConsiderationChipSx}

              />

              <Chip

                label={`${summary.approvedProspects} approved prospects`}

                sx={teamApprovedChipSx}

              />

            </Stack>

          </Stack>



          <Box

            sx={{

              display: "grid",

              gridTemplateColumns: {

                xs: "repeat(2, 1fr)",

                md: "repeat(5, 1fr)",

              },

              gap: 1,

              mt: 2,

            }}

          >

            {[

              [

                "First-time",

                `${summary.firstTime} · ${summary.firstTimePercentage}%`,

              ],

              ["Experienced", String(summary.experienced)],

              ["Host parish", String(summary.hostParish)],

              [

                "Deanery",

                initial.deaneryDataAvailable

                  ? String(summary.deanery)

                  : "Not available",

              ],

              ["Broader community", String(summary.broaderCommunity)],

            ].map(([label, value]) => (

              <Box key={label} sx={teamStatBoxSx}>

                <Typography

                  sx={{ fontSize: "0.75rem", color: TEAM_MUTED, lineHeight: 1.4 }}

                >

                  {label}

                </Typography>

                <Typography

                  sx={{

                    fontSize: "1rem",

                    fontWeight: 700,

                    color: TEAM_TEXT,

                    lineHeight: 1.3,

                    mt: 0.25,

                  }}

                >

                  {value}

                </Typography>

              </Box>

            ))}

          </Box>



          <TeamInfoAlert>{TEAM_SIZE_GUIDANCE_TEXT}</TeamInfoAlert>



          {!initial.deaneryDataAvailable && (

            <TeamInfoAlert>{DEANERY_UNAVAILABLE_TEXT}</TeamInfoAlert>

          )}

        </TeamSectionPaper>



        <TeamLeadershipPanel

          assignments={assignments}

          spiritualMentorOptions={initial.leadership.spiritualMentorOptions}

          spiritualDirectorOptions={initial.leadership.spiritualDirectorOptions}

          onAssign={assignLeadership}

        />



        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
          <Typography component="span" sx={teamOverlineSx}>
            Review routing
          </Typography>
          <Typography sx={teamPanelHeadingSx}>
            Approval Authority: {authorityLabel}
          </Typography>
          {initial.authority.reason && (
            <TeamInfoAlert sx={{ mt: 1 }}>
              {initial.authority.reason} This is an escalation path, not an error.
            </TeamInfoAlert>
          )}
          <Typography sx={{ ...teamPanelSubtextSx, mt: 1 }}>
            Retreat Liaison: {initial.retreatLiaison ?? "Not assigned"}
          </Typography>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}
          >
            {initial.authority.recipients.map((recipient) => (
              <Chip
                key={`${recipient.role}-${recipient.displayName}`}
                size="small"
                variant="outlined"
                sx={teamRecipientChipSx}
                label={
                  <>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {recipient.role}:
                    </Box>{" "}
                    {recipient.displayName}
                    {recipient.email ? "" : " · email missing"}
                  </>
                }
              />
            ))}
          </Stack>
        </Paper>

        <Box component="section" aria-labelledby="candidate-directory-title">

          <Typography

            id="candidate-directory-title"

            sx={teamSectionTitleSx}

          >

            Candidate Directory

          </Typography>

          <Typography sx={{ ...teamBodySx, mt: 0.5 }}>

            Automatically ordered by eligible history and parish context. No

            geography group excludes an otherwise eligible couple.

          </Typography>



          <Stack spacing={2} sx={{ mt: 1.5 }}>

            {groupedCandidates.map(([label, group]) => (

              <Box key={label}>

                <Typography

                  sx={{

                    fontSize: "1rem",

                    fontWeight: 700,

                    color: TEAM_TEXT,

                    mb: 0.75,

                  }}

                >

                  {label}

                </Typography>

                <Stack spacing={1}>

                  {group.map((candidate) => (

                    <CandidateCard

                      key={candidate.coupleId}

                      candidate={candidate}

                      onToggle={(checked) =>

                        toggleConsideration(candidate.coupleId, checked)

                      }

                    />

                  ))}

                </Stack>

              </Box>

            ))}

          </Stack>



          {candidates.length > pageSize && (

            <Pagination

              page={page}

              count={pageCount}

              onChange={(_, value) => setPage(value)}

              sx={{ mt: 2 }}

              aria-label="Candidate Directory pages"

            />

          )}

        </Box>



        <ProspectiveTeamReview

          parishName={parishName}

          seasonLabel={seasonLabel}

          inConsiderationCount={summary.inConsideration}

          authorityLabel={authorityLabel}

          reviews={reviews}

          candidates={candidates}

          onPrepareReview={handlePrepareReview}

        />

      </Stack>

    </TeamFormationShell>

  );

}


